import { Suspense, useEffect, useState, useTransition } from 'react';

import { useQueryLoader } from 'react-relay';

import { templateInitialSearchResultsContentfulRelayQuery } from '@/__generated_contentful__/templateInitialSearchResultsContentfulRelayQuery.graphql';
import { templatePaginatedSearchResultsContentfulRelayRefetchableFragment$key } from '@/__generated_contentful__/templatePaginatedSearchResultsContentfulRelayRefetchableFragment.graphql';
import { SkeletonTemplateRecommendationCardGrid } from '@/components/confluence/templates/skeleton-template-card-grid';

import {
	CategorySearchSuggestion,
	TemplateCategorySearchResults,
} from '../../components/contentful-relay-components/template-category-search-results-contentful-relay';
import {
	TemplateFuzzySearchResults,
	TemplateSearchSuggestion,
} from '../../components/contentful-relay-components/template-fuzzy-search-results-contentful-relay';
import {
	parseSearchQuery,
	TemplateInitialSearchResultsRelayQuery,
} from '../../components/contentful-relay-components/template-initial-search-results-contentful-relay';
import { TemplateRecommendationsSearchResults } from '../../components/template-recommendations-search-results-contentful-relay';
import { DEFAULT_CATEGORY_SLUG } from '../../hooks/use-category-slug';
import { useQueryLocale } from '../../hooks/use-query-locale';

export type TemplateSearchResultsSectionProps = {
	preloadedSearchResults?: templatePaginatedSearchResultsContentfulRelayRefetchableFragment$key | null;
	searchTerm: string | CategorySearchSuggestion | TemplateSearchSuggestion | null;
	searchIntent?: string | CategorySearchSuggestion | TemplateSearchSuggestion | null;
};

const CATEGORY = 'category';
const SEARCH = 'search';
const RECOMMENDED = 'recommended';

const isSearchType = (
	searchTerm: CategorySearchSuggestion | TemplateSearchSuggestion | string | null,
): searchTerm is string | TemplateSearchSuggestion => {
	return typeof searchTerm === 'string' || searchTerm?.type === 'template';
};

const isCategoryType = (
	searchTerm: CategorySearchSuggestion | TemplateSearchSuggestion | string | null,
): searchTerm is CategorySearchSuggestion => {
	return typeof searchTerm !== 'string' && searchTerm?.type === 'category';
};

/**
 * Create a unique key to identify the search term.
 * This is used to force a new instance
 * of `TemplatePaginatedSearchResults`
 * when the search term changes.
 */
const getSearchKey = (
	searchTerm: string | TemplateSearchSuggestion | CategorySearchSuggestion | null,
): string => {
	if (isCategoryType(searchTerm)) {
		return searchTerm.id;
	}
	if (isSearchType(searchTerm)) {
		return typeof searchTerm === 'string' ? searchTerm : searchTerm.id;
	}
	return RECOMMENDED;
};

type TemplateSearchResultsState =
	| {
			type: typeof CATEGORY;
			key: string;
			term: CategorySearchSuggestion;
			hasPreloadedSearchResults: boolean;
	  }
	| {
			type: typeof SEARCH;
			key: string;
			term: string | TemplateSearchSuggestion;
	  }
	| { type: typeof RECOMMENDED; key: string }
	| { type: null };

const getInitialState = (
	searchTerm: TemplateSearchResultsSectionProps['searchTerm'],
	preloadedSearchResults: TemplateSearchResultsSectionProps['preloadedSearchResults'],
): TemplateSearchResultsState => {
	if (preloadedSearchResults != null && isCategoryType(searchTerm)) {
		return {
			type: CATEGORY,
			key: getSearchKey(searchTerm),
			term: searchTerm,
			hasPreloadedSearchResults: true,
		};
	}
	return { type: null };
};

const getNextState = (
	state: TemplateSearchResultsState,
	searchTerm: TemplateSearchResultsSectionProps['searchTerm'],
): TemplateSearchResultsState => {
	const newKey = getSearchKey(searchTerm);
	if (state.type == null || newKey !== state?.key) {
		if (isCategoryType(searchTerm)) {
			return {
				type: CATEGORY,
				key: newKey,
				term: searchTerm,
				hasPreloadedSearchResults: false,
			};
		} else if (isSearchType(searchTerm)) {
			return {
				type: SEARCH,
				key: newKey,
				term: searchTerm,
			};
		} else {
			return {
				key: newKey,
				type: RECOMMENDED,
			};
		}
	}
	return state;
};

export const TemplateSearchResultsSection = ({
	preloadedSearchResults,
	searchTerm,
	searchIntent,
}: TemplateSearchResultsSectionProps) => {
	const [searchTypePending, startSearchTypeTransition] = useTransition();
	const locale = useQueryLocale();

	const [state, setState] = useState(() => getInitialState(searchTerm, preloadedSearchResults));

	const [, preloadSearchIntent] = useQueryLoader<templateInitialSearchResultsContentfulRelayQuery>(
		TemplateInitialSearchResultsRelayQuery,
	);

	useEffect(() => {
		if (state.type == null || getSearchKey(searchTerm) !== state.key) {
			startSearchTypeTransition(() => {
				setState((prevState) => getNextState(prevState, searchTerm));
			});
		}
	}, [searchTerm, state]);

	useEffect(() => {
		if (searchIntent && isCategoryType(searchIntent)) {
			if (searchIntent.id != DEFAULT_CATEGORY_SLUG) {
				const searchQuery = parseSearchQuery(searchIntent);
				preloadSearchIntent({
					urlSlugs: searchQuery.whereClause?.['urlSlugs'] ?? [],
					templateIds: searchQuery.whereClause?.['templateIds'] ?? [],
					categoryUrlSlugs: searchQuery.whereClause?.['categoryUrlSlugs'] ?? [],
					limit: searchQuery.first ?? 9,
					skip: 0,
					locale: locale,
				});
			}
		}
	}, [locale, preloadSearchIntent, searchIntent]);

	return searchTypePending ? (
		<SkeletonTemplateRecommendationCardGrid />
	) : (
		<Suspense fallback={<SkeletonTemplateRecommendationCardGrid />}>
			{state.type === RECOMMENDED ? (
				<TemplateRecommendationsSearchResults />
			) : state.type === SEARCH ? (
				<TemplateFuzzySearchResults searchKey={state.key} searchTerm={state.term} />
			) : state.type === CATEGORY ? (
				<TemplateCategorySearchResults
					searchKey={state.key}
					searchTerm={state.term}
					preloadedSearchResults={state.hasPreloadedSearchResults ? preloadedSearchResults : null}
				/>
			) : (
				<SkeletonTemplateRecommendationCardGrid />
			)}
		</Suspense>
	);
};
